/**
 * Plan vs actual — the card between the KPI strip and Top 3 actions.
 *
 * THE CARD FOLLOWS THE PAGE'S DATE PICKER. A monthly target is pro-rated to the
 * exact days on screen, per month rather than as one blended daily rate, so a
 * window crossing a month boundary takes each month's own daily share. Days in
 * a month the plan file does not cover are counted and NAMED rather than
 * quietly treated as a zero target — otherwise a window carrying eleven
 * unplanned days of sales against nineteen days of target reads 122%.
 *
 * FULL MONTH is the one deliberate exception, and it says so on the card: it
 * holds the whole month's target and carries the run rate across the month,
 * which answers "will we land it" rather than "are we on pace today".
 *
 * The plan is written on MRP — GMV there is MRP × units, not realised sales —
 * so achievement is only meaningful against MRP actuals. On the SP basis the
 * card warns instead of converting.
 */
import { type ReactNode, useState } from "react";
import { Card, Kpi, Kpis, fmt } from "../ds";
import { useReport } from "../useReport";
import {
  type Basis,
  type OpsWindow,
  FixedWindow,
  Seg,
  bandColor,
  daysInMonth,
  monthLabel,
  num,
  periodLabel,
  str,
} from "./opsLib";
import type { DateRange } from "@/lib/dateRange";

/** One (sub-line, month) cell of the plan grid, as the API returns it. */
interface Cell {
  subline: string;
  month: string;
  planGmv: number;
  planUnits: number;
  actualGmv: number;
  actualUnits: number;
  /** Days of this month the WINDOW covers. */
  elapsed: number;
  monthDays: number;
  /** Days of this month the feed has actually landed, at most `elapsed`. */
  daysWithData: number;
}

const cellsOf = (rows: Array<Record<string, unknown>>): Cell[] =>
  rows.map((r) => ({
    subline: str(r["subline"]) || "—",
    month: str(r["month"]),
    planGmv: num(r["plan_gmv"]) ?? 0,
    planUnits: num(r["plan_units"]) ?? 0,
    actualGmv: num(r["actual_gmv"]) ?? 0,
    actualUnits: num(r["actual_units"]) ?? 0,
    elapsed: num(r["elapsed_days"]) ?? 0,
    monthDays: num(r["month_days"]) ?? 0,
    daysWithData: num(r["days_with_data"]) ?? 0,
  }));

/**
 * How much of a month's target the days on screen are owed.
 *
 * A month the window covers end to end contributes its WHOLE target. A month
 * the window covers end to end but whose feed is short is pro-rated on the days
 * that exist — taking the full target against partial actuals would invent a
 * shortfall. A month still running is divided by its days and multiplied by the
 * days on screen. The straight-line split is an assumption, not the client's
 * own phasing: it will read a seasonally soft or heavy week as a miss or a beat.
 */
function shareOf(m: {
  elapsed: number;
  monthDays: number;
  daysWithData: number;
}): number {
  if (m.monthDays <= 0) return 0;
  const windowWhole = m.elapsed >= m.monthDays;
  if (windowWhole && m.daysWithData < m.monthDays && m.daysWithData > 0) {
    return m.daysWithData / m.monthDays;
  }
  return Math.min(m.elapsed, m.monthDays) / m.monthDays;
}

type PlanView = "period" | "month";

/** Achievement colour: green at or above plan, amber within ten points, red
 *  below, grey when there is no plan to divide by. */
const tone = (v: number | null): string =>
  v === null
    ? "var(--ink-3)"
    : v >= 100
      ? "var(--up)"
      : v >= 90
        ? "var(--warn)"
        : "var(--down)";

const Money = ({
  v,
  positiveIsBad,
}: {
  v: number;
  positiveIsBad?: boolean;
}) => (
  <span
    style={{
      color: (positiveIsBad ? v > 0 : v < 0) ? "var(--down)" : "var(--up)",
    }}
  >
    {v > 0 ? "" : "+"}
    {fmt(Math.abs(v), "money")}
  </span>
);

export function PlanStrip({
  customer,
  range,
  basis,
}: {
  customer: string;
  range: DateRange;
  basis: Basis;
}) {
  const [view, setView] = useState<PlanView>("period");
  const q = useReport("/v1/reports/ops/plan-actual", customer, range);

  const seg = (
    <Seg<PlanView>
      value={view}
      options={[
        { id: "period", label: "Selected period" },
        { id: "month", label: "Full month" },
      ]}
      onChange={setView}
    />
  );

  if (q.isLoading) {
    return (
      <section className="card">
        <div className="card-head">
          <span className="card-title">Plan vs actual</span>
        </div>
        <div className="empty">Loading the plan…</div>
      </section>
    );
  }

  const w: OpsWindow | undefined = q.data?.window;
  const scopeWindow = periodLabel(w?.start, w?.end);

  /*
   * A missing plan is not an error state to hide. The store answers 503 when no
   * sub-line map exists for the brand, which is a configuration gap with a name
   * — so the message is shown in the card rather than collapsing the page.
   */
  if (q.isError) {
    return (
      <section className="card">
        <div className="card-head">
          <span className="card-title">Plan vs actual</span>
          <span className="scope-pill">{scopeWindow}</span>
          <span className="spacer" />
          <span className="asof">Plan on MRP</span>
        </div>
        <div className="empty">{q.error.message}</div>
      </section>
    );
  }

  const cells = cellsOf(q.data?.rows ?? []);
  const version = q.data?.plan_version;

  /*
   * Months are split into planned and unplanned BEFORE any arithmetic.
   *
   * The API returns a cell for every (sub-line, month) on either side of the
   * grid, so a month with sales and no target arrives with plan 0 — and its
   * actuals would otherwise inflate the numerator of a ratio whose denominator
   * has no target in it at all. Unplanned months are removed from BOTH sides
   * and named on the card.
   */
  const monthKeys = [...new Set(cells.map((c) => c.month))].sort();
  const plannedMonths = monthKeys.filter(
    (m) =>
      cells.filter((c) => c.month === m).reduce((a, c) => a + c.planGmv, 0) > 0,
  );
  const uncovered = monthKeys
    .filter((m) => !plannedMonths.includes(m))
    .map((m) => {
      const any = cells.find((c) => c.month === m);
      return { month: m, days: any?.elapsed ?? 0 };
    })
    .filter((u) => u.days > 0);

  // "Full month" resolves to the month the window ends in, as the published
  // page does; the switcher is meaningless for a month with no target.
  const endMonth = w?.end.slice(0, 7) ?? "";
  const full = view === "month";
  const scopeMonths = full
    ? plannedMonths.filter((m) => m === endMonth)
    : plannedMonths;
  const inScope = cells.filter((c) => scopeMonths.includes(c.month));

  if (inScope.length === 0) {
    return (
      <section className="card">
        <div className="card-head">
          <span className="card-title">Plan vs actual</span>
          <span className="scope-pill">
            {full ? (endMonth ? monthLabel(endMonth) : "—") : scopeWindow}
          </span>
          {seg}
          <span className="spacer" />
          <span className="asof">Plan on MRP</span>
        </div>
        <div className="empty">
          No target for{" "}
          {full && endMonth ? monthLabel(endMonth) : "this window"} in the plan
          file. Targets are set per calendar month under Settings &rarr;
          Targets.
        </div>
      </section>
    );
  }

  /* ── the arithmetic, per month so nothing is blended across a boundary ── */

  const monthMeta = (
    m: string,
  ): { elapsed: number; monthDays: number; daysWithData: number } => {
    const any = inScope.find((c) => c.month === m);
    return {
      elapsed: any?.elapsed ?? 0,
      monthDays: any?.monthDays ?? daysInMonth(m),
      // The widest any listing saw is how much of the month landed.
      daysWithData: Math.max(
        0,
        ...inScope.filter((c) => c.month === m).map((c) => c.daysWithData),
      ),
    };
  };

  const planOf = (c: Cell): number =>
    full ? c.planGmv : c.planGmv * shareOf(monthMeta(c.month));
  const planUnitsOf = (c: Cell): number =>
    full ? c.planUnits : c.planUnits * shareOf(monthMeta(c.month));
  /** In the full-month view the actual becomes a forecast: this month's own
   *  daily rate over the days on screen, carried across all its days. That is
   *  arithmetic, not a model — it assumes the rest of the month looks like the
   *  part already run. */
  const actualOf = (c: Cell): number => {
    if (!full) return c.actualGmv;
    const m = monthMeta(c.month);
    return m.elapsed > 0 ? (c.actualGmv / m.elapsed) * m.monthDays : 0;
  };
  const actualUnitsOf = (c: Cell): number => {
    if (!full) return c.actualUnits;
    const m = monthMeta(c.month);
    return m.elapsed > 0 ? (c.actualUnits / m.elapsed) * m.monthDays : 0;
  };

  const planNow = inScope.reduce((a, c) => a + planOf(c), 0);
  const planUnits = inScope.reduce((a, c) => a + planUnitsOf(c), 0);
  const actual = inScope.reduce((a, c) => a + actualOf(c), 0);
  const actUnits = inScope.reduce((a, c) => a + actualUnitsOf(c), 0);
  const pace = planNow > 0 ? (actual / planNow) * 100 : null;
  // Gap is PLAN MINUS ACTUAL: positive means behind. The API returns the
  // opposite sign under the same name, so it is derived here rather than read.
  const gap = planNow - actual;

  const scopeDays = scopeMonths.reduce((a, m) => a + monthMeta(m).elapsed, 0);
  const scopeMonthDays = scopeMonths.reduce(
    (a, m) => a + monthMeta(m).monthDays,
    0,
  );
  /** The measured daily pace over the days on screen — the only rate this card
   *  extrapolates from, and it is stated wherever it is used. */
  const dailyRate =
    scopeDays > 0
      ? inScope.reduce((a, c) => a + c.actualGmv, 0) / scopeDays
      : 0;

  /** The human-readable account of what the plan figure is made of. */
  const planBasis = scopeMonths
    .map((m) => {
      const meta = monthMeta(m);
      return meta.elapsed >= meta.monthDays &&
        meta.daysWithData >= meta.monthDays
        ? `${monthLabel(m)} in full`
        : `${Math.min(meta.daysWithData || meta.elapsed, meta.monthDays)} of ${meta.monthDays} days in ${monthLabel(m)}`;
    })
    .join(" + ");

  /** Closed months whose feed is short — pro-rated on the days that exist. */
  const shortData = scopeMonths
    .map((m) => ({ month: m, ...monthMeta(m) }))
    .filter(
      (m) =>
        m.elapsed >= m.monthDays &&
        m.daysWithData > 0 &&
        m.daysWithData < m.monthDays,
    );

  const scope = full
    ? monthLabel(scopeMonths[0] ?? endMonth)
    : periodLabel(
        scopeMonths.length && w
          ? maxDate(w.start, `${scopeMonths[0] ?? ""}-01`)
          : w?.start,
        scopeMonths.length && w
          ? minDate(
              w.end,
              lastDayOf(scopeMonths[scopeMonths.length - 1] ?? endMonth),
            )
          : w?.end,
      );

  /* ── sub-line rows, sorted by planned GMV ── */
  const subNames = [...new Set(inScope.map((c) => c.subline))];
  const subRows = subNames
    .map((name) => {
      const own = inScope.filter((c) => c.subline === name);
      const plan = own.reduce((a, c) => a + planOf(c), 0);
      const act = own.reduce((a, c) => a + actualOf(c), 0);
      return {
        name,
        plan,
        planUnits: own.reduce((a, c) => a + planUnitsOf(c), 0),
        actual: act,
        units: own.reduce((a, c) => a + actualUnitsOf(c), 0),
        pct: plan > 0 ? (act / plan) * 100 : null,
        gap: plan - act,
      };
    })
    .sort((a, b) => b.plan - a.plan);

  return (
    <Card
      title="Plan vs actual"
      scope={scope}
      // ds's Card puts `actions` after the spacer, so the view switcher sits on
      // the right of the header rather than beside the scope pill as it does on
      // the published page. Deliberate: the card chrome stays ds's to own.
      actions={seg}
      asof={`Plan on MRP · data to ${w?.as_of ?? "—"}`}
      foot={
        <>
          <b>The plan is set on MRP</b>, as the workbook states &mdash; GMV
          there is MRP &times; units, not realised sales &mdash; so achievement
          is only meaningful on the MRP basis.{" "}
          {full ? (
            <>
              <b>
                Full month is the one view that steps outside the page picker
              </b>
              , deliberately: it holds {monthLabel(scopeMonths[0] ?? endMonth)}
              &rsquo;s whole target and carries the {fmt(dailyRate, "money")} a
              day measured over the {scopeDays} day
              {scopeDays === 1 ? "" : "s"} on screen across all {scopeMonthDays}{" "}
              days. That is arithmetic, not a model &mdash; it assumes the rest
              of the month looks like the part already run, and it extrapolates
              from the window you picked, not from the whole month to date.{" "}
            </>
          ) : (
            <>
              <b>This card follows the picker at the top of the page</b>, like
              every other view. Plan here is <b>{planBasis}</b>: a month the
              window covers end to end contributes its whole target, and only a
              month still running is divided by its days and multiplied by the
              days on screen. Plan and actual always cover the identical window.
              The straight-line split of a part-month is an assumption, not the
              client&rsquo;s own phasing &mdash; it will read a seasonally soft
              or heavy week as a miss or a beat.{" "}
            </>
          )}
          Actuals are the partner&rsquo;s own portal download at MRP, the same
          measurement the GMV tile above uses, so the two agree.{" "}
          {version ? (
            <>
              Measured against <b>plan v{version.version}</b>
              {version.created_by ? `, set by ${version.created_by}` : ""}.{" "}
            </>
          ) : (
            <>No saved plan version is attached to these targets. </>
          )}
          Market share is against O&rsquo;cean&rsquo;s RTD Hydration category
          (same as MCP). OSA is absolute (not weighted). ASP is a selling price.{" "}
          <b>Click a sub-line to see its SKUs.</b>
        </>
      }
    >
      {basis === "sp" && (
        <FixedWindow block>
          The plan is written on MRP. On the SP basis this comparison carries
          roughly ten points of routine discounting and would show a miss that
          is not one &mdash; switch the basis to MRP to read it. The figures
          below stay on MRP for that reason.
        </FixedWindow>
      )}
      {!full && uncovered.length > 0 && (
        <FixedWindow block>
          <b>
            {scopeDays} of the{" "}
            {scopeDays + uncovered.reduce((a, u) => a + u.days, 0)} days on
            screen carry a target.
          </b>{" "}
          {uncovered
            .map(
              (u) =>
                `${u.days} day${u.days === 1 ? "" : "s"} in ${monthLabel(u.month)}`,
            )
            .join(" and ")}{" "}
          {uncovered.length === 1 && uncovered[0]?.days === 1
            ? "falls"
            : "fall"}{" "}
          outside the plan file. Those days are excluded from <b>both</b> the
          plan and the actual, so this card reads {scope} &mdash; the GMV tile
          above still covers the full window you selected.
        </FixedWindow>
      )}
      {!full && shortData.length > 0 && (
        <FixedWindow block>
          {shortData
            .map(
              (x) =>
                `${monthLabel(x.month)} is a closed month but the feed holds only ${x.daysWithData} of its ${x.monthDays} days`,
            )
            .join("; ")}
          , so it is pro-rated on the days that exist rather than taking the
          whole target &mdash; a full target against partial actuals would
          invent a shortfall.
        </FixedWindow>
      )}

      <div style={{ padding: "0 18px" }}>
        <Kpis>
          <Kpi
            label={
              full
                ? `Plan GMV (MRP), ${monthLabel(scopeMonths[0] ?? endMonth)}`
                : `Plan GMV (MRP), ${scope}`
            }
            value={fmt(planNow, "money")}
            title={
              full
                ? "The whole month's target from the client's workbook, summed across the sub-lines"
                : "The monthly target pro-rated to the days selected above — each day carries its own month's daily share"
            }
            foot={
              full
                ? `${fmt(planUnits, "int")} units at MRP`
                : `${fmt(planUnits, "int")} units · ${planBasis}`
            }
          />
          <Kpi
            label={
              full
                ? "Actual GMV (MRP), full month"
                : "Actual GMV (MRP), this period"
            }
            value={fmt(actual, "money")}
            title={
              full
                ? "The daily rate over the days on screen carried across the full month — arithmetic, not a model"
                : "Portal actuals for the selected period — the same window as the plan beside it"
            }
            foot={
              full
                ? `${fmt(dailyRate, "money")} / day × ${scopeMonthDays} days`
                : `${scopeDays} day${scopeDays === 1 ? "" : "s"} · ${fmt(actUnits, "int")} units`
            }
          />
          <Kpi
            label="GMV achievement"
            value={
              <span style={{ color: tone(pace) }}>
                {pace === null ? "—" : `${pace.toFixed(1)}%`}
              </span>
            }
            title="Actual ÷ plan over the identical window"
            foot={
              full
                ? `against the whole ${monthLabel(scopeMonths[0] ?? endMonth)} target`
                : `${scopeDays} of ${scopeMonthDays} planned days`
            }
          />
          <Kpi
            label={gap > 0 ? "GMV gap to plan" : "GMV ahead of plan"}
            value={<Money v={gap} positiveIsBad />}
            title="Plan minus actual over the same window"
            foot={full ? "on the current run rate" : "over this period"}
          />
        </Kpis>
      </div>

      <PlanTable
        subRows={subRows}
        planNow={planNow}
        actual={actual}
        pace={pace}
        gap={gap}
        planUnits={planUnits}
        actUnits={actUnits}
        skus={q.data?.portfolio_skus ?? []}
        skuPlan={q.data?.sku_plan ?? {}}
        showDeltas={!full && w?.comparison_available !== false}
        proRate={
          full
            ? 1.0
            : scopeMonths.reduce((a, m) => a + shareOf(monthMeta(m)), 0) /
              (scopeMonths.length || 1)
        }
      />

      {q.data?.unmapped && q.data.unmapped.units > 0 && (
        <FixedWindow block>
          {fmt(q.data.unmapped.gmv, "money")} on{" "}
          {fmt(q.data.unmapped.units, "int")} units could not be placed against
          a planned sub-line and is excluded from the actuals above:{" "}
          {q.data.unmapped.sku_names.join(", ")}. Add them to the sub-line map,
          or to the plan, before reading the gap as real.
        </FixedWindow>
      )}
    </Card>
  );
}

/* ── the 13-column Plan vs Actual table (ticket 14zkq6bch6u) ─────────── */

interface PortfolioSku {
  sku_name: string;
  product_id: string;
  subline: string;
  gmv_mrp: number;
  units: number;
  osa: number | null;
  asp: number | null;
  discount_pct: number | null;
  market_share: number | null;
  prev_gmv_mrp: number;
  prev_units: number;
  prev_osa: number | null;
  prev_asp: number | null;
  prev_discount_pct: number | null;
  prev_market_share: number | null;
  fill_rate: number | null;
}

/** Small delta text under a value. kind = how to format the diff. */
function D({
  cur,
  prev,
  good = "up",
  kind = "pp",
}: {
  cur: number | null;
  prev: number | null;
  good?: "up" | "down" | "neutral";
  /** 'pp' = percentage points, 'money' = ₹ formatted, 'int' = integer, 'price' = ₹ price */
  kind?: "pp" | "money" | "int" | "price";
}): ReactNode {
  if (cur === null || prev === null) return null;
  const d = cur - prev;
  const rounded = Math.round(d * 10) / 10;
  if (rounded === 0)
    return (
      <div className="delta flat" style={{ fontSize: "0.8em" }}>
        {kind === "pp" ? "0.0pp" : kind === "money" ? "₹0" : "0"}
      </div>
    );
  const color =
    good === "neutral"
      ? "var(--ink-3)"
      : (good === "down" ? d < 0 : d > 0)
        ? "var(--up)"
        : "var(--down)";
  const sign = d > 0 ? "+" : "";
  const label =
    kind === "money"
      ? `${sign}${fmt(d, "money")}`
      : kind === "int"
        ? `${sign}${fmt(Math.round(d), "int")}`
        : kind === "price"
          ? `${sign}${fmt(d, "price")}`
          : `${sign}${rounded.toFixed(1)}pp`;
  return <div style={{ fontSize: "0.8em", color }}>{label}</div>;
}

function PlanTable({
  subRows,
  planNow,
  actual,
  pace,
  gap,
  planUnits,
  actUnits,
  skus,
  skuPlan,
  showDeltas,
  proRate,
}: {
  subRows: Array<{
    name: string;
    plan: number;
    planUnits: number;
    actual: number;
    units: number;
    pct: number | null;
    gap: number;
  }>;
  planNow: number;
  actual: number;
  pace: number | null;
  gap: number;
  planUnits: number;
  actUnits: number;
  skus: PortfolioSku[];
  skuPlan: Record<string, { units: number; gmv: number }>;
  showDeltas: boolean;
  /** Pro-rate factor: how much of the month the window covers. */
  proRate: number;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (name: string) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Pool SKUs by sub-line for crawl metrics
  const skusBySubline = new Map<string, PortfolioSku[]>();
  for (const s of skus) {
    const arr = skusBySubline.get(s.subline) ?? [];
    arr.push(s);
    skusBySubline.set(s.subline, arr);
  }

  // Pool sub-line crawl metrics (OSA, ASP, discount, market share)
  const sublineCrawl = (name: string) => {
    const group = skusBySubline.get(name) ?? [];
    if (group.length === 0)
      return {
        osa: null,
        asp: null,
        discount: null,
        mkt: null,
        fillRate: null as number | null,
        prevGmv: 0,
        prevUnits: 0,
        prevOsa: null,
        prevAsp: null,
        prevDiscount: null,
        prevMkt: null,
      } as const;
    // OSA = weighted by units (proxy for store-days)
    const totalUnits = group.reduce(
      (a, s) => a + (s.osa !== null ? s.units : 0),
      0,
    );
    const osa =
      totalUnits > 0
        ? group.reduce(
            (a, s) => a + (s.osa ?? 0) * (s.osa !== null ? s.units : 0),
            0,
          ) / totalUnits
        : null;
    // ASP = SUM(sp_sales) / SUM(units) — use asp × units as proxy
    const aspUnits = group.filter((s) => s.asp !== null && s.units > 0);
    const asp =
      aspUnits.length > 0
        ? aspUnits.reduce((a, s) => a + (s.asp ?? 0) * s.units, 0) /
          aspUnits.reduce((a, s) => a + s.units, 0)
        : null;
    // Discount = pooled from all SKUs
    const mkt = group.reduce((a, s) => a + (s.market_share ?? 0), 0) || null;
    const discount = group.reduce(
      (a, s) => a + (s.discount_pct !== null ? s.discount_pct * s.units : 0),
      0,
    );
    const discDenom = group.reduce(
      (a, s) => a + (s.discount_pct !== null ? s.units : 0),
      0,
    );
    // Previous period
    const prevTotalUnits = group.reduce(
      (a, s) => a + (s.prev_osa !== null ? s.prev_units : 0),
      0,
    );
    const prevOsa =
      prevTotalUnits > 0
        ? group.reduce(
            (a, s) =>
              a + (s.prev_osa ?? 0) * (s.prev_osa !== null ? s.prev_units : 0),
            0,
          ) / prevTotalUnits
        : null;
    const prevAspUnits = group.filter(
      (s) => s.prev_asp !== null && s.prev_units > 0,
    );
    const prevAsp =
      prevAspUnits.length > 0
        ? prevAspUnits.reduce(
            (a, s) => a + (s.prev_asp ?? 0) * s.prev_units,
            0,
          ) / prevAspUnits.reduce((a, s) => a + s.prev_units, 0)
        : null;
    const prevMkt =
      group.reduce((a, s) => a + (s.prev_market_share ?? 0), 0) || null;
    const prevDiscount = group.reduce(
      (a, s) =>
        a +
        (s.prev_discount_pct !== null ? s.prev_discount_pct * s.prev_units : 0),
      0,
    );
    const prevDiscDenom = group.reduce(
      (a, s) => a + (s.prev_discount_pct !== null ? s.prev_units : 0),
      0,
    );
    return {
      osa,
      asp,
      discount: discDenom > 0 ? discount / discDenom : null,
      mkt,
      // Fill rate: show if any SKU has it. Can't pool correctly without raw
      // delivered/ordered, so show null if mixed (frontend shows "—").
      fillRate: (() => {
        const withFr = group.filter((s) => s.fill_rate !== null);
        if (withFr.length === 0) return null;
        // Simple average as proxy — exact pooling needs raw PO atoms
        return (
          withFr.reduce((a, s) => a + (s.fill_rate ?? 0), 0) / withFr.length
        );
      })(),
      prevGmv: group.reduce((a, s) => a + s.prev_gmv_mrp, 0),
      prevUnits: group.reduce((a, s) => a + s.prev_units, 0),
      prevOsa,
      prevAsp,
      prevDiscount: prevDiscDenom > 0 ? prevDiscount / prevDiscDenom : null,
      prevMkt,
    };
  };

  const ach = (act: number, plan: number): ReactNode => {
    if (plan <= 0) return "—";
    const v = (act / plan) * 100;
    return (
      <span style={{ color: tone(v), fontWeight: 600 }}>{v.toFixed(1)}%</span>
    );
  };

  const gapCell = (plan: number, act: number): ReactNode => {
    if (plan <= 0 && act > 0)
      return <span style={{ color: "var(--up)" }}>+{fmt(act, "money")}</span>;
    if (plan <= 0) return "—";
    const g = plan - act;
    return <Money v={g} positiveIsBad />;
  };

  return (
    <div className="table-wrap" style={{ overflowX: "auto" }}>
      <table style={{ minWidth: 1200 }}>
        <thead>
          <tr>
            <th>Sub-line / SKU</th>
            <th className="num">Plan GMV</th>
            <th className="num">Actual GMV</th>
            <th className="num">GMV Ach.</th>
            <th className="num">GMV Gap</th>
            <th className="num">Plan Units</th>
            <th className="num">Actual Units</th>
            <th className="num">Mkt Share</th>
            <th className="num">OSA %</th>
            <th className="num">ASP</th>
            <th className="num">Disc. %</th>
            <th className="num">Fill rate</th>
          </tr>
        </thead>
        <tbody>
          {subRows.map((r) => {
            const open = expanded.has(r.name);
            const c = sublineCrawl(r.name);
            const slSkus = (skusBySubline.get(r.name) ?? []).sort(
              (a, b) => b.gmv_mrp - a.gmv_mrp,
            );
            return (
              <>
                {/* Fragment for sub-line + its SKUs */}
                <tr
                  key={r.name}
                  className="clickable"
                  onClick={() => {
                    toggle(r.name);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <div className="rowlabel-name">
                      {open ? "▾" : "▸"} {r.name}
                    </div>
                    <div className="rowlabel-sub">
                      {slSkus.length} SKU{slSkus.length === 1 ? "" : "s"}
                    </div>
                  </td>
                  <td className="num">{fmt(r.plan, "money")}</td>
                  <td className="num">
                    {fmt(r.actual, "money")}
                    {showDeltas && (
                      <D
                        cur={r.actual}
                        prev={c.prevGmv}
                        good="up"
                        kind="money"
                      />
                    )}
                  </td>
                  <td className="num">{ach(r.actual, r.plan)}</td>
                  <td className="num">{gapCell(r.plan, r.actual)}</td>
                  <td className="num">{fmt(r.planUnits, "int")}</td>
                  <td className="num">
                    {fmt(r.units, "int")}
                    {showDeltas && (
                      <D
                        cur={r.units}
                        prev={c.prevUnits}
                        good="up"
                        kind="int"
                      />
                    )}
                  </td>
                  <td className="num">
                    {c.mkt !== null ? `${c.mkt.toFixed(1)}%` : "—"}
                    {showDeltas && <D cur={c.mkt} prev={c.prevMkt} kind="pp" />}
                  </td>
                  <td className="num">
                    <span style={{ color: bandColor(c.osa) }}>
                      {c.osa !== null ? `${c.osa.toFixed(1)}%` : "—"}
                    </span>
                    {showDeltas && <D cur={c.osa} prev={c.prevOsa} kind="pp" />}
                  </td>
                  <td className="num">
                    {c.asp !== null ? fmt(c.asp, "price") : "—"}
                    {showDeltas && (
                      <D
                        cur={c.asp}
                        prev={c.prevAsp}
                        good="neutral"
                        kind="price"
                      />
                    )}
                  </td>
                  <td className="num">
                    {c.discount !== null ? `${c.discount.toFixed(1)}%` : "—"}
                    {showDeltas && (
                      <D
                        cur={c.discount}
                        prev={c.prevDiscount}
                        good="down"
                        kind="pp"
                      />
                    )}
                  </td>
                  <td className="num">
                    {c.fillRate !== null ? (
                      <span style={{ color: bandColor(c.fillRate) }}>
                        {c.fillRate.toFixed(1)}%
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
                {open &&
                  slSkus.map((s) => (
                    <tr
                      key={s.sku_name}
                      style={{ background: "var(--bg-1, #fafbfd)" }}
                    >
                      <td style={{ paddingLeft: 32 }}>
                        <div
                          className="rowlabel-name"
                          style={{ fontSize: "0.9em" }}
                        >
                          {s.sku_name}
                        </div>
                        <div className="rowlabel-sub">{s.subline}</div>
                      </td>
                      {(() => {
                        const sp = skuPlan[s.product_id];
                        const skuPlanGmv = (sp?.gmv ?? 0) * proRate;
                        const skuPlanUnits = (sp?.units ?? 0) * proRate;
                        const skuAch =
                          skuPlanGmv > 0
                            ? (s.gmv_mrp / skuPlanGmv) * 100
                            : null;
                        const skuGap =
                          skuPlanGmv > 0 ? skuPlanGmv - s.gmv_mrp : null;
                        return (
                          <>
                            <td className="num">
                              {skuPlanGmv > 0 ? fmt(skuPlanGmv, "money") : "—"}
                            </td>
                            <td className="num">
                              {fmt(s.gmv_mrp, "money")}
                              {showDeltas && (
                                <D
                                  cur={s.gmv_mrp}
                                  prev={s.prev_gmv_mrp}
                                  good="up"
                                  kind="money"
                                />
                              )}
                            </td>
                            <td className="num">
                              {skuAch !== null ? (
                                <span style={{ color: tone(skuAch) }}>
                                  {skuAch.toFixed(1)}%
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="num">
                              {skuGap !== null ? (
                                <Money v={skuGap} positiveIsBad />
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="num">
                              {skuPlanUnits > 0
                                ? fmt(skuPlanUnits, "int")
                                : "—"}
                            </td>
                            <td className="num">
                              {fmt(s.units, "int")}
                              {showDeltas && (
                                <D
                                  cur={s.units}
                                  prev={s.prev_units}
                                  good="up"
                                  kind="int"
                                />
                              )}
                            </td>
                          </>
                        );
                      })()}
                      <td className="num">
                        {s.market_share !== null
                          ? `${s.market_share.toFixed(1)}%`
                          : "—"}
                        {showDeltas && (
                          <D
                            cur={s.market_share}
                            prev={s.prev_market_share}
                            kind="pp"
                          />
                        )}
                      </td>
                      <td className="num">
                        <span style={{ color: bandColor(s.osa) }}>
                          {s.osa !== null ? `${s.osa.toFixed(1)}%` : "—"}
                        </span>
                        {showDeltas && (
                          <D cur={s.osa} prev={s.prev_osa} kind="pp" />
                        )}
                      </td>
                      <td className="num">
                        {s.asp !== null ? fmt(s.asp, "price") : "—"}
                        {showDeltas && (
                          <D
                            cur={s.asp}
                            prev={s.prev_asp}
                            good="neutral"
                            kind="price"
                          />
                        )}
                      </td>
                      <td className="num">
                        {s.discount_pct !== null
                          ? `${s.discount_pct.toFixed(1)}%`
                          : "—"}
                        {showDeltas && (
                          <D
                            cur={s.discount_pct}
                            prev={s.prev_discount_pct}
                            good="down"
                            kind="pp"
                          />
                        )}
                      </td>
                      <td className="num">
                        {s.fill_rate !== null ? (
                          <span style={{ color: bandColor(s.fill_rate) }}>
                            {s.fill_rate.toFixed(1)}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
              </>
            );
          })}
          <tr className="is-total">
            <td>
              <b>All sub-lines</b>
            </td>
            <td className="num">
              <b>{fmt(planNow, "money")}</b>
            </td>
            <td className="num">
              <b>{fmt(actual, "money")}</b>
            </td>
            <td className="num">
              <b style={{ color: tone(pace) }}>
                {pace === null ? "—" : `${pace.toFixed(1)}%`}
              </b>
            </td>
            <td className="num">
              <b>
                <Money v={gap} positiveIsBad />
              </b>
            </td>
            <td className="num">
              <b>{fmt(planUnits, "int")}</b>
            </td>
            <td className="num">
              <b>{fmt(actUnits, "int")}</b>
            </td>
            <td className="num"></td>
            <td className="num"></td>
            <td className="num"></td>
            <td className="num"></td>
            <td className="num"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ── tiny date helpers, local because only this card re-bounds a window ── */
const maxDate = (a: string, b: string): string => (a > b ? a : b);
const minDate = (a: string, b: string): string => (a < b ? a : b);
const lastDayOf = (ym: string): string =>
  `${ym}-${String(daysInMonth(ym)).padStart(2, "0")}`;
